import { collection, getDocs, query, where, orderBy, getDoc, doc, serverTimestamp, Timestamp, setDoc, updateDoc } from "firebase/firestore";

import { getAuthUserHospital, getAuthUserId } from "./AuthService";
import { createAuthAccount, updateAuthAccount, generatePatientRole } from "./FunctionService";
import { createReminder } from "./ReminderService";
import { DoctorRoles, MuscleStrengthToFitnessLevel, PatientRoles, Phase, ReminderType, Role } from "../config/DiactiveTypes";
import { calculateDaysRemaining, determinePatientStatus } from "../utils/DiactiveUtils";
import { firestore } from "./FirebaseService";
import { getUserFullName } from "./UserService";

const usersCollectionName = "users";
const usersCollection = collection(firestore, usersCollectionName);
const evalCollectionName = "evaluations";

export const addPatient = async (userData, evalData) => {
  const doctorId = getAuthUserId();
  const hospitalId = await getAuthUserHospital();
  const patientNumber = await generatePatientNumber(hospitalId);
  const patientRole = await generatePatientRole(hospitalId);

  try {
    const patientId = await createAuthAccount(userData.email, userData.firstName, patientRole, userData.nickname);

    delete userData.countdownStartedAt;
    userData = {
      ...userData,
      dateOfBirth: Timestamp.fromDate(new Date(userData.dateOfBirth)),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      doctor: doctorId,
      hospital: hospitalId,
      patientNumber: patientNumber,
      role: patientRole,
      ageAtSignUp: evalData.age,
      fitnessLevel: MuscleStrengthToFitnessLevel[evalData.muscleStrength],
      hasAppAccess: patientRole === Role.Intr25,
      canTrain: false,
      isInStudy: true,
      hasFilledQsPre: false,
      hasFilledQsPost: false,
      level: 1,
      profilePictureUpdatedAt: 0,
      progression: 0,
      xp: 0
    };

    const userDocRef = doc(firestore, usersCollectionName, patientId);
    await setDoc(userDocRef, userData);

    evalData = {
      ...evalData,
      diseaseOnsetDate: Timestamp.fromDate(new Date(evalData.diseaseOnsetDate)),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: doctorId,
    };

    const evalDocRef = doc(firestore, usersCollectionName, patientId, evalCollectionName, Phase.PRE);
    await setDoc(evalDocRef, evalData);

    await createReminder(patientId, ReminderType.UploadGlucoseFilePre);
    // Not shown in RemindersPanel
    await createReminder(patientId, ReminderType.UploadGlucoseFilePreFollowUp);
    // For patient (not shown in RemindersPanel)
    await createReminder(patientId, ReminderType.FillQuestionnairesPre);

    return patientId;
  }
  catch (error) {
    throw new Error(error.message);
  }
};

export const updatePatient = async (patientId, patientRole, userData, evalData, phase) => {
  try {
    const countdownStartedAtRaw = userData.countdownStartedAt;

    userData = {
      ...userData,
      dateOfBirth: Timestamp.fromDate(new Date(userData.dateOfBirth)),
      updatedAt: serverTimestamp()
    };

    if (countdownStartedAtRaw) {
      const selectedDate = new Date(countdownStartedAtRaw);
      const now = new Date();
      selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
      userData.countdownStartedAt = Timestamp.fromDate(selectedDate);
    } else {
      delete userData.countdownStartedAt;
    }

    evalData = {
      ...evalData,
      diseaseOnsetDate: Timestamp.fromDate(new Date(evalData.diseaseOnsetDate)),
      updatedAt: serverTimestamp()
    };

    const evalDocRef = doc(firestore, usersCollectionName, patientId, evalCollectionName, phase);
    const evalDocSnap = await getDoc(evalDocRef);
    const isNewEval = !evalDocSnap.exists();
    const isNewPostEval = phase === Phase.POST && isNewEval;

    if (isNewEval) {
      evalData.createdAt = serverTimestamp();
      evalData.createdBy = getAuthUserId();
    }

    await updateAuthAccount(patientId, userData.email, userData.firstName, patientRole, userData.nickname);

    const userDocRef = doc(firestore, usersCollectionName, patientId);
    await updateDoc(userDocRef, userData);

    await setDoc(evalDocRef, evalData, { merge: true });

    if (isNewPostEval) {
      await createReminder(patientId, ReminderType.UploadGlucoseFilePost);
      // Not shown in RemindersPanel
      await createReminder(patientId, ReminderType.UploadGlucoseFilePostFollowUp);
      // For patient (not shown in RemindersPanel)
      await createReminder(patientId, ReminderType.FillQuestionnairesPost);
    }

    return true;
  }
  catch (error) {
    throw new Error(error.message);
  }
};

export const enablePatientAppAccess = async (patientId) => {
  const docRef = doc(firestore, usersCollectionName, patientId);
  await updateDoc(docRef, {
    hasAppAccess: true,
    updatedAt: serverTimestamp()
  });
  return true;
};

export const enablePatientCanTrain = async (patientId) => {
  const docRef = doc(firestore, usersCollectionName, patientId);
  await updateDoc(docRef, {
    canTrain: true,
    updatedAt: serverTimestamp()
  });
};

export const startPatientCountdown = async (patientId, doctorId) => {
  const docRef = doc(firestore, usersCollectionName, patientId);
  const timestamp = serverTimestamp();
  await updateDoc(docRef, {
    countdownStartedAt: timestamp,
    updatedAt: timestamp
  });

  await createReminder(patientId, ReminderType.ScheduleAppointmentPost);
  await createReminder(patientId, ReminderType.CompletePostEval);

  return timestamp;
};

export const removePatientFromStudy = async (patientId, removedFromStudyBy = getAuthUserId()) => {
  const docRef = doc(firestore, usersCollectionName, patientId);
  await updateDoc(docRef, {
    isInStudy: false,
    removedFromStudyBy: removedFromStudyBy ?? null,
    updatedAt: serverTimestamp()
  });
  return true;
};

export const markPatientAsDeleted = async (patientId, deletedBy = getAuthUserId()) => {
  await removePatientFromStudy(patientId, deletedBy);
  const docRef = doc(firestore, usersCollectionName, patientId);
  await updateDoc(docRef, {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: deletedBy ?? null,
    updatedAt: serverTimestamp()
  });
  return true;
};

export const getPatientData = async (role, patientId, options = {}) => {
  const { hospitalIdOverride } = options;
  if (PatientRoles.includes(role) && patientId !== getAuthUserId()) {
    throw new Error("No tienes permiso para acceder a los datos de este paciente");
  }

  const docRef = doc(firestore, usersCollectionName, patientId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error(`No se ha encontrado el paciente ${patientId}`);
  }

  if (docSnap.get("isDeleted")) {
    throw new Error(`No se ha encontrado el paciente ${patientId}`);
  }

  if (DoctorRoles.includes(role)) {
    const hospital = hospitalIdOverride ?? await getAuthUserHospital();
    if (docSnap.get("hospital") !== hospital) {
      throw new Error(`No tienes permiso para acceder al paciente ${patientId}`);
    }
  }

  let userData = docSnap.data();
  if (!PatientRoles.includes(userData.role)) {
    throw new Error(`El paciente ${patientId} no es válido`);
  }

  const doctorName = await getUserFullName(userData.doctor);
  const removedFromStudyByName = userData.removedFromStudyBy
    ? await getUserFullName(userData.removedFromStudyBy)
    : null;
  const daysRemaining = calculateDaysRemaining(userData.countdownStartedAt, userData.isInStudy);
  const glucoseFileCount = userData.glucoseFileCount ?? { pre: 0, post: 0 };
  const status = determinePatientStatus(
    userData.countdownStartedAt,
    daysRemaining,
    userData.hasFilledQsPre,
    userData.hasFilledQsPost,
    glucoseFileCount,
    userData.isInStudy
  );

  userData = {
    ...userData,
    id: docSnap.id,
    doctorName,
    removedFromStudyByName,
    daysRemaining,
    glucoseFileCount,
    status,
  };

  const enrichEvalData = async (raw) => {
    if (!raw || Object.keys(raw).length === 0) {
      return {};
    }
    const evalCreatedByName = raw.createdBy
      ? await getUserFullName(raw.createdBy)
      : null;
    return { ...raw, evalCreatedByName };
  };

  const preEvalDocRef = doc(firestore, usersCollectionName, patientId, evalCollectionName, Phase.PRE);
  const preEvalDocSnap = await getDoc(preEvalDocRef);
  const preEvalData = await enrichEvalData(preEvalDocSnap.exists() ? preEvalDocSnap.data() : {});

  const postEvalDocRef = doc(firestore, usersCollectionName, patientId, evalCollectionName, Phase.POST);
  const postEvalDocSnap = await getDoc(postEvalDocRef);
  const postEvalData = await enrichEvalData(postEvalDocSnap.exists() ? postEvalDocSnap.data() : {});

  return { userData, preEvalData, postEvalData };
};

export const getPatientHasAppAccess = async (patientId) => {
  const docRef = doc(firestore, usersCollectionName, patientId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.get("hasAppAccess") || false;
  }
  return false;
};

export const getAllPatients = async (role, options = {}) => {
  const { hospitalIdOverride } = options;
  const filters = [
    where("role", "in", PatientRoles),
    orderBy("createdAt")
  ];

  if (role === Role.Doctor) {
    const hospitalId = hospitalIdOverride ?? await getAuthUserHospital();
    filters.push(where("hospital", "==", hospitalId));
  }

  const q = query(usersCollection, ...filters);
  const querySnapshot = await getDocs(q);
  const showPersonal = role === Role.SuperUser || role === Role.Doctor;
  const activePatientDocs = querySnapshot.docs.filter((doc) => doc.get("isDeleted") !== true);

  const users = activePatientDocs.map((doc) => {
    const patientData = doc.data();
    const daysRemaining = calculateDaysRemaining(patientData.countdownStartedAt, patientData.isInStudy);
    const glucoseFileCount = patientData.glucoseFileCount ?? { pre: 0, post: 0 };
    const questionnairesCount = patientData.questionnairesCount ?? { pre: 0, post: 0 };
    const status = determinePatientStatus(
      patientData.countdownStartedAt,
      daysRemaining,
      patientData.hasFilledQsPre,
      patientData.hasFilledQsPost,
      glucoseFileCount,
      patientData.isInStudy
    );

    return {
      id: doc.id,
      firstName: showPersonal ? patientData.firstName : undefined,
      lastName: showPersonal ? patientData.lastName : undefined,
      profilePicture: showPersonal ? patientData.profilePicture : undefined,
      nickname: patientData.nickname,
      patientNumber: patientData.patientNumber,
      hospital: patientData.hospital,
      role: patientData.role,
      level: patientData.level,
      createdAt: patientData.createdAt,
      countdownStartedAt: patientData.countdownStartedAt,
      daysRemaining,
      glucoseFileCount,
      questionnairesCount,
      hasPendingMail: patientData.hasPendingMail === true,
      workoutsThisWeek: patientData.workoutsThisWeek ?? 0,
      status
    };
  });

  return users;
};

const generatePatientNumber = async (hospitalId) => {
  const q = query(
    usersCollection,
    where("hospital", "==", hospitalId),
    where("role", "in", PatientRoles),
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.length + 1;
};
